import { useMemo } from "react";
import useUser from "./user";
import { Gender, s3Dir, UserState } from "../types";
import {
  useParticipantTracks,
  useRemoteParticipants,
} from "@livekit/components-react";
import { Track } from "livekit-client";

export const useSelectedVoice = (locale: string, id?: string) => {
  const [user] = useUser();
  return useMemo(() => {
    if (user.metadata.aiVoice && user.metadata.aiVoiceGender) {
      const rank = Number.parseInt(user.metadata.aiVoice.replace("voice", ""));
      const gender = id
        ? id.includes("female")
          ? "female"
          : "male"
        : undefined;
      return {
        id: id || `${user.metadata.aiVoiceGender} ${rank}`,
        rank,
        gender: (gender || user.metadata.aiVoiceGender) as Gender,
        previewUrl: `${s3Dir}voiceSamples/${user.metadata.aiVoiceGender}/${locale}/voice${rank}.mp3`,
      };
    }
    return undefined;
  }, [user, locale, id]);
};

export const useInterpreter = () => {
  const [user] = useUser();
  const remoteParticipants = useRemoteParticipants();
  return useMemo(() => {
    return remoteParticipants.find((participant) => {
      if (!participant.metadata) {
        return false;
      }
      const metadata = JSON.parse(participant.metadata) as {
        language: UserState["metadata"]["selectedLanguage"];
        participant_type: UserState["metadata"]["participantType"];
      };
      if (
        metadata.participant_type === "interpreter" &&
        metadata.language === user.metadata.selectedLanguage
      ) {
        return true;
      }
    });
  }, [remoteParticipants, user.metadata.selectedLanguage]);
};

export const useInterpreterTrack = () => {
  const interpreter = useInterpreter();

  const interpreterTracks = useParticipantTracks(
    [Track.Source.Microphone],
    interpreter?.identity
  );

  return useMemo(() => {
    if (!interpreter) {
      return;
    }
    const interpreterTrack = interpreterTracks.find(
      (t) => t.participant.sid === interpreter.sid
    );
    return interpreterTrack;
  }, [interpreter, interpreterTracks]);
};

export const useInterpreterInRoomAndUnmuted = () => {
  const interpreterTrack = useInterpreterTrack();
  return useMemo(() => {
    return interpreterTrack && !interpreterTrack.publication.isMuted;
  }, [interpreterTrack]);
};
